import { useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';

import type { OperationResult } from 'urql';
import { useClient } from 'urql';
import { concat, pipe, subscribe, take } from 'wonka';

import { config } from '../../../config/config';
import { exists } from '../../shared/utils/filters';
import type {
    IGetUserBalanceQuery,
    IUserWallets,
    IWalletUpdated,
    IWalletUpdatedSubscription,
    IWalletUpdatedSubscriptionVariables,
} from '../queries/balance.graphql';
import { GetUserBalanceDocument, WalletUpdatedDocument } from '../queries/balance.graphql';
import type { IWallet } from '../store/account.store';
import { accountStore, selectHasController, selectWallets } from '../store/account.store';

function convertUpdate(update: IWalletUpdated): IWallet {
    return {
        walletId: update.walletId,
        type: update.type,
        isLocked: update.current?.isLocked,
        isActive: update.current?.isActive,
        balanceAmount: update.current?.balanceAmount,
        currencyCode: update.currencyCode,
        aggregates: update.current?.aggregates,
        restrictions: {
            monetaryDebit: (
                update.current?.restrictions?.additionalProperties?.['monetary-debit'] ?? []
            ).map((restriction: { Amount: string; CurrencyCode: string; ExpiresOn: string }) => {
                return {
                    amount: +(restriction.Amount ?? 0) ?? 0,
                    expiresOn: restriction.ExpiresOn,
                    currencyCode: restriction.CurrencyCode,
                };
            }),
        },
    };
}
function convertInitialWallets(wallets: IUserWallets[]): IWallet[] {
    return wallets.map((item) => {
        return {
            walletId: item.walletId,
            type: item.type,
            isLocked: item.isLocked,
            isActive: item.isActive,
            balanceAmount: item.balanceAmount,
            currencyCode: item.currencyCode,
            aggregates: item.aggregates,
            restrictions: {
                monetaryDebit: (item?.restrictions?.['monetary-debit'] ?? []).map(
                    (restriction: { amount: string; currencyCode: string; expiresOn: string }) => {
                        return {
                            amount: +(restriction.amount ?? 0) ?? 0,
                            expiresOn: restriction.expiresOn,
                            currencyCode: restriction.currencyCode,
                        };
                    },
                ),
            },
        } as IWallet;
    });
}
export function useBalance() {
    const hasController = useSelector(selectHasController);
    const dispatch = useDispatch();
    const auth = useAuth();
    const client = useClient();
    const userId = auth.user?.profile?.user_profile_id as string;
    const [isController, setIsController] = useState(false);

    useEffect(() => {
        if (hasController || !userId) {
            return;
        }
        setIsController(true);
        dispatch(accountStore.actions.setHasController(true));
    }, [hasController, isController, userId, dispatch]);

    useEffect(() => {
        return () => {
            if (!isController) {
                return;
            }
            setIsController(false);
            dispatch(accountStore.actions.setHasController(false));
        };
    }, [isController, dispatch]);

    useEffect(() => {
        if (!isController || !userId || !config.TENANT_ID) {
            return;
        }
        const queryStream = client.query<IGetUserBalanceQuery>(
            GetUserBalanceDocument,
            {
                userProfileId: userId,
                xTenantID: config.TENANT_ID,
            },
            { requestPolicy: 'network-only' },
        );
        const subscription = client.subscription<
            IWalletUpdatedSubscription,
            IWalletUpdatedSubscriptionVariables
        >(WalletUpdatedDocument, {
            userProfileId: userId,
        });

        const stream = pipe(
            concat<
                OperationResult<IGetUserBalanceQuery> | OperationResult<IWalletUpdatedSubscription>
            >([pipe(queryStream, take(1)), subscription]),
            subscribe((result) => {
                const data = result.data ?? {};
                if ('userWallets' in data && data.userWallets) {
                    dispatch(
                        accountStore.actions.setWallets(
                            convertInitialWallets(data.userWallets?.filter(exists)),
                        ),
                    );
                }

                if ('walletUpdated' in data && data.walletUpdated) {
                    dispatch(
                        accountStore.actions.updateWalletBalance(
                            convertUpdate(data?.walletUpdated),
                        ),
                    );
                }
            }),
        );
        return () => {
            stream.unsubscribe();
        };
    }, [client, dispatch, isController, userId]);
    const wallets = useSelector(selectWallets);
    return useMemo(
        () => ({
            wallets,
        }),
        [wallets],
    );
}
